import { useState } from 'react'
import { KeyboardArrowDown } from '@mui/icons-material'
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from '@mui/material'
import './SelectInput.scss'
import { useI18n } from '@ggs/i18n'

/**
 * @typedef {import("@mui/material").SelectChangeEvent} SelectChangeEvent
 */

/**
 * @typedef {Object} SelectOption
 * @property {string} label - Option label string
 * @property {string} value - Option value
 * @property {boolean=} disabled - Is option disabled?
 */

/**
 * @typedef {Object} SelectFieldProps
 * @property {string} id
 * @property {boolean=} fullWidth
 * @property {boolean=} error
 * @property {Object=} formControlFields
 * @property {Object<string, string>=} formControlClasses
 * @property {string=} label
 * @property {Object<string, string>=} labelClasses
 * @property {string=} placeholder
 * @property {boolean=} multiple
 * @property {SelectOption[]=} selectOptions
 * @property {boolean=} allowEmpty
 * @property {string=} emptyLabel
 * @property {Object=} inputProps
 * @property {Object=} inputFields
 * @property {Object<string, string>=} inputClasses
 * @property {string=} helperText
 * @property {string=} errorHelperText
 * @property {Object=} helperTextFields
 * @property {Object<string, string>=} helperTextClasses
 * @property {Object<string, string>=} listClasses
 * @property {Object<string, string>=} listItemClasses
 */

/**
 * A component for an input field
 * @param {SelectFieldProps} props
 * @return {JSX.Element}
 */
const SelectInput = ({
  id,
  fullWidth = true,
  error = false,
  formControlFields = {},
  formControlClasses = {
    root: '',
  },
  label = '',
  labelClasses = {
    root: '',
    focused: '',
  },
  placeholder = 'Placeholder text',
  multiple = false,
  selectOptions = [],
  allowEmpty = true,
  emptyLabel,
  inputProps = {},
  inputFields = {},
  inputClasses = {
    root: '',
    select: '',
    icon: '',
  },
  helperText = '',
  errorHelperText = '',
  helperTextFields = {},
  helperTextClasses = {
    root: '',
  },
  listClasses = {
    root: '',
    inner: '',
  },
  listItemClasses = {
    root: '',
    inner: '',
  },
}) => {
  const { t } = useI18n()
  const [selected, setSelected] = useState(inputFields.value ?? [])

  /**
   *
   * @param {SelectChangeEvent} e
   */
  const handleChange = (e) => {
    const value = e.target.value
    setSelected(typeof value === 'string' ? value.split(', ') : value)
    // @ts-ignore
    if (inputFields.onChange)
      // @ts-ignore
      inputFields.onChange(e)
  }

  return (
    <FormControl
      {...formControlFields}
      variant="outlined"
      hiddenLabel={true}
      fullWidth={fullWidth}
      error={error}
      classes={{
        ...formControlClasses,
        root: `a-select-control ${formControlClasses.root ?? ''}`.trim(),
      }}
    >
      {label && (
        <FormLabel
          id={`${id}-label`}
          htmlFor={id}
          classes={{
            ...labelClasses,
            root: `a-select-control__label ${labelClasses.root ?? ''}`.trim(),
            focused: `a-select-control__focused ${labelClasses.focused ?? ''}`.trim(),
          }}
        >
          {label}
        </FormLabel>
      )}
      <Select
        labelId={`${id}-label`}
        id={id}
        label={label}
        aria-describedby={`${id}-helper-text`}
        IconComponent={KeyboardArrowDown}
        multiple={multiple}
        notched={false}
        displayEmpty={multiple || allowEmpty}
        renderValue={(value) => {
          if (
            (typeof value === 'string' && value === '') ||
            (Array.isArray(value) && value.length === 0)
          )
            return placeholder
          else if (multiple && Array.isArray(value)) return value.join(', ')
          else return selectOptions.find((option) => option.value === value)?.label ?? placeholder
        }}
        inputProps={{
          ...inputProps,
          classes: {
            notchedOutline: 'a-select-control__outline',
          },
        }}
        MenuProps={{
          classes: {
            root: `a-select-list ${listClasses.root ?? ''}`.trim(),
            paper: `a-select-list__inner ${listClasses.inner ?? ''}`.trim(),
          },
        }}
        className={`a-select-control__root ${inputClasses.root ?? ''}`.trim()}
        classes={{
          ...inputClasses,
          select: `a-select-control__select ${inputClasses.select ?? ''}`.trim(),
          icon: `a-select-control__icon ${inputClasses.icon ?? ''}`.trim(),
        }}
        {...inputFields}
        onChange={handleChange}
      >
        {!multiple && allowEmpty && (
          <MenuItem
            value=""
            disabled={!allowEmpty}
            classes={{
              root: `a-select-list__list-item ${listItemClasses.root ?? ''}`.trim(),
              focusVisible: 'a-select-list__list-item--focused',
              selected: 'a-select-list__list-item--selected',
            }}
          >
            <ListItemText
              primary={emptyLabel || t('None')}
              classes={{ primary: 'a-select-list__list-item-text a-select-list__list-item-empty' }}
            />
          </MenuItem>
        )}
        {selectOptions.map((option, id) => (
          <MenuItem
            value={option.value}
            key={id}
            classes={{
              root: `a-select-list__list-item ${listItemClasses.root ?? ''}`.trim(),
              focusVisible: `a-select-list__list-item--focused ${
                listItemClasses.focusVisible ?? ''
              }`.trim(),
              selected: `a-select-list__list-item--selected ${
                listItemClasses.selected ?? ''
              }`.trim(),
            }}
            disabled={option.disabled}
          >
            {multiple && (
              <Checkbox
                size="small"
                checked={selected.indexOf(option.value) !== -1}
                classes={{ root: 'a-select-list__checkbox' }}
              />
            )}
            <ListItemText
              primary={option.label}
              classes={{ primary: 'a-select-list__list-item-text' }}
            />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText
        {...helperTextFields}
        id={`${id}-helper-text`}
        classes={{
          ...helperTextClasses,
          root: `a-select-control__helper-text ${helperTextClasses.root ?? ''}`.trim(),
        }}
      >
        {error ? errorHelperText || t('Error occurred') : helperText}
      </FormHelperText>
    </FormControl>
  )
}

export default SelectInput
